import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {formatISO} from "date-fns";
import {companiesPaginationInfoSelector} from "store/company/companySelectors";
import {candidatesPaginationInfoSelector} from "store/candidate/candidateSelectors";
import {PaginationInfo, UseUserBehaviorTrackHookProps} from "hooks/useUserBehaviorTrackHook/types";
import {TRootState} from "types";
import {TFilterReducerState} from "store/filter/types";

const useUserBehaviorTrackHook = ({
	itemId,
	itemType = "company",
	source = "project",
	position,
	isRecommended = false,
	trackForSecond = 3,
	defaultEvent = "appear",
}: UseUserBehaviorTrackHookProps) => {
	// Use a ref to persist the timeout across renders.
	const trackTimer = useRef<number | null>(null);

	// Replace 'any' with the appropriate state type if available.
	const {company_filters, candidate_filters} = useSelector(
		(state: TRootState) => state.filter as TFilterReducerState,
	);

	const companiesPaginationInfo: PaginationInfo | undefined = useSelector(
		companiesPaginationInfoSelector,
	);
	const candidatesPaginationInfo: PaginationInfo | undefined = useSelector(
		candidatesPaginationInfoSelector,
	);

	const [event, setEvent] = useState(defaultEvent);
	const [filterOptions, setFilterOptions] = useState(null);
	const [interacted, setInteracted] = useState(false);
	const [targetListId, setTargetListId] = useState<string | number | null>(null);

	const currentRef = useRef(true);

	const trackStart = () => {
		if (!interacted && currentRef.current) {
			setEvent("appear");
			trackTimer.current = window.setTimeout(() => {
				setInteracted(true);
			}, trackForSecond * 1000);
		}
	};

	const trackComplete = () => {
		if (trackTimer.current !== null) {
			clearTimeout(trackTimer.current);
			trackTimer.current = null;
		}
	};

	const behaviorType = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const object = isRecommended ? "Recommended" : "Non-Recommended";
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const subject = `${object} ${itemType}`;

		switch (event) {
			case "hover":
				return `${subject} hovered`;
			case "click":
				return `${subject} clicked`;
			case "add":
				return `${subject} added to target list`;
			case "remove":
				return `${subject} removed from target list`;
			case "expanded":
				return `${subject} card expanded in the list`;
			case "details_viewed":
				return `${subject} profile details viewed`;
			default:
				return `${subject} appeared on the screen`;
		}
	}, [itemType, isRecommended, event]);

	const getPurifiedFilterData = useCallback((): unknown | null => {
		if (filterOptions === null) return null;

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const filterDetails = {...filterOptions};
		let updatedFilter: {[key: string]: string[]} | null = null;
		Object.keys(filterDetails).forEach((key) => {
			const data = filterDetails[key];
			if (Array.isArray(data) && data.length) {
				if (updatedFilter === null) {
					updatedFilter = {};
				}
				updatedFilter[key] = data;
			}
		});

		return updatedFilter;
	}, [filterOptions]);

	const totalResults = useMemo(() => {
		if (itemType === "company") {
			return companiesPaginationInfo ? companiesPaginationInfo.count : 0;
		} else {
			return candidatesPaginationInfo ? candidatesPaginationInfo.count : 0;
		}
	}, [candidatesPaginationInfo, companiesPaginationInfo, itemType]);

	const itemEventDetails = useMemo(() => {
		const time = formatISO(Date.now());

		const details = {
			id: itemId,
			filter: getPurifiedFilterData(),
			recommended: isRecommended,
			target_list_id: targetListId,
			total_results: totalResults,
			enhanced_mode: isRecommended ? "on" : "off",
			time,
			source,
			position,
		};

		return {type: behaviorType, details, event};
	}, [
		itemId,
		getPurifiedFilterData,
		isRecommended,
		targetListId,
		totalResults,
		source,
		position,
		behaviorType,
		event,
	]);

	useEffect(() => {
		if (interacted) {
			setEvent("hover");
		}

		return () => {
			if (trackTimer.current !== null) {
				clearTimeout(trackTimer.current);
			}
			currentRef.current = false;
		};
	}, [interacted]);

	useEffect(() => {
		if (itemType === "company") {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setFilterOptions({...company_filters});
		} else {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setFilterOptions({...candidate_filters});
		}

		return () => {
			currentRef.current = false;
		};
	}, [itemType, company_filters, candidate_filters]);

	return {
		trackStart,
		trackComplete,
		setEvent,
		setTargetListId,
		itemEventDetails,
	};
};

export default useUserBehaviorTrackHook;
